import {FC} from 'react'
import {Card, Form, Row, Col, Button, Spinner} from 'react-bootstrap'
import {LegalTinaDetail} from '../core/_models'

type Props = {
  isLoadingLegal: boolean
  legalOrigin: LegalTinaDetail
  legalPending: LegalTinaDetail
  isDifferent: (val1: any, val2: any) => boolean
  handleChange: (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>
  ) => void
  handleSelectChange: (e: React.ChangeEvent<HTMLSelectElement>) => void
  handleCancel: () => void
  handleSubmit: () => void
  isLoadingButton: boolean
  isFormValid: boolean
}

const LegalTinaComparisonForm: FC<Props> = ({
  isLoadingLegal,
  legalOrigin,
  legalPending,
  isDifferent,
  handleChange,
  handleSelectChange,
  handleCancel,
  handleSubmit,
  isLoadingButton,
  isFormValid,
}) => {
  return (
    <Row className='mb-5'>
      <Col>
        <Card>
          <Card.Header className='card-header-stretch'>
            <Card.Title className='w-100'>
              <div className='title-container w-100 d-flex justify-content-between align-items-center'>
                <div className='title d-flex align-items-center'>
                  <span className='fa fa-balance-scale px-2'></span>
                  <h3 className='text-gray-700'>Review Legal TINA Differences</h3>
                </div>
              </div>
            </Card.Title>
          </Card.Header>
          <Card.Body>
            {!isLoadingLegal ? (
              <Form className='form-compare-legal'>
                {/* HEADERS */}
                <Row className='mb-4 pb-2 border-bottom'>
                  <Col md={6}>
                    <h4 className='text-muted text-center'>Origin Content</h4>
                  </Col>
                  <Col md={6}>
                    <h4 className='text-primary text-center'>Pending Review Content</h4>
                  </Col>
                </Row>

                {/* GENERAL INFORMATION */}
                <h3 className='mb-4 mt-5'>General Information</h3>

                <Row className='mb-4'>
                  <Col md={6}>
                    <Form.Label className='text-muted me-0 ps-0' column>
                      Unique Code
                    </Form.Label>
                    <Form.Control
                      disabled
                      placeholder='Enter unique code'
                      type='text'
                      value={legalOrigin.unique_code || ''}
                    />
                  </Col>
                  <Col md={6}>
                    <Form.Label className='text-primary me-0 ps-0' column>
                      <span
                        className={`required ${
                          isDifferent(legalOrigin.unique_code, legalPending.unique_code)
                            ? 'label-highlight'
                            : ''
                        }`}
                      >
                        Unique Code
                      </span>
                    </Form.Label>
                    <Form.Control
                      placeholder='Enter unique code'
                      type='text'
                      name='unique_code'
                      className='required'
                      value={legalPending.unique_code || ''}
                      onChange={handleChange}
                    />
                  </Col>
                </Row>

                <Row className='mb-4'>
                  <Col md={6}>
                    <Form.Label className='text-muted me-0 ps-0' column>
                      Heading
                    </Form.Label>
                    <Form.Control
                      disabled
                      placeholder='Enter heading'
                      type='text'
                      value={legalOrigin.heading || ''}
                    />
                  </Col>
                  <Col md={6}>
                    <Form.Label className='text-primary me-0 ps-0' column>
                      <span
                        className={`required ${
                          isDifferent(legalOrigin.heading, legalPending.heading)
                            ? 'label-highlight'
                            : ''
                        }`}
                      >
                        Heading
                      </span>
                    </Form.Label>
                    <Form.Control
                      placeholder='Enter heading'
                      type='text'
                      name='heading'
                      className='required'
                      value={legalPending.heading || ''}
                      onChange={handleChange}
                    />
                  </Col>
                </Row>

                <Row className='mb-4'>
                  <Col md={6}>
                    <Form.Label className='text-muted me-0 ps-0' column>
                      Description
                    </Form.Label>
                    <Form.Control
                      disabled
                      as='textarea'
                      rows={3}
                      placeholder='Enter description'
                      value={legalOrigin.description || ''}
                    />
                  </Col>
                  <Col md={6}>
                    <Form.Label className='text-primary me-0 ps-0' column>
                      <span
                        className={
                          isDifferent(legalOrigin.description, legalPending.description)
                            ? 'label-highlight'
                            : ''
                        }
                      >
                        Description
                      </span>
                    </Form.Label>
                    <Form.Control
                      as='textarea'
                      rows={3}
                      placeholder='Enter description'
                      name='description'
                      value={legalPending.description || ''}
                      onChange={handleChange}
                    />
                  </Col>
                </Row>

                <hr />
                {/* ATTRIBUTES */}
                <h3 className='mb-4 mt-5'>Attributes</h3>

                <Row className='mb-4'>
                  <Col md={6}>
                    <Form.Label className='text-muted me-0 ps-0' column>
                      Level
                    </Form.Label>
                    <Form.Control
                      disabled
                      type='number'
                      placeholder='Enter level'
                      min={0}
                      value={legalOrigin.level ?? ''}
                    />
                  </Col>
                  <Col md={6}>
                    <Form.Label className='text-primary me-0 ps-0' column>
                      <span
                        className={`required ${
                          isDifferent(legalOrigin.level, legalPending.level)
                            ? 'label-highlight'
                            : ''
                        }`}
                      >
                        Level
                      </span>
                    </Form.Label>
                    <Form.Control
                      type='number'
                      placeholder='Enter level'
                      name='level'
                      min={0}
                      className='required'
                      value={legalPending.level ?? ''}
                      onChange={handleChange}
                    />
                  </Col>
                </Row>

                <Row className='mb-4'>
                  <Col md={6}>
                    <Form.Label className='text-muted me-0 ps-0' column>
                      Divider
                    </Form.Label>
                    <Form.Select disabled value={legalOrigin.divider ? '1' : '0'}>
                      <option value='' disabled>
                        Select divider
                      </option>
                      <option value='1'>Yes</option>
                      <option value='0'>No</option>
                    </Form.Select>
                  </Col>
                  <Col md={6}>
                    <Form.Label className='text-primary me-0 ps-0' column>
                      <span
                        className={
                          isDifferent(legalOrigin.divider, legalPending.divider)
                            ? 'label-highlight'
                            : ''
                        }
                      >
                        Divider
                      </span>
                    </Form.Label>
                    <Form.Select
                      name='divider'
                      value={
                        legalPending.divider === 1 || legalPending.divider === true
                          ? '1'
                          : legalPending.divider === 0 || legalPending.divider === false
                          ? '0'
                          : ''
                      }
                      onChange={handleSelectChange}
                    >
                      <option value='' disabled>
                        Select divider
                      </option>
                      <option value='1'>Yes</option>
                      <option value='0'>No</option>
                    </Form.Select>
                  </Col>
                </Row>

                <Row className='mb-4'>
                  <Col md={6}>
                    <Form.Label className='text-muted me-0 ps-0' column>
                      Sub Issue
                    </Form.Label>
                    <Form.Select disabled value={legalOrigin.sub_issue ? '1' : '0'}>
                      <option value='' disabled>
                        Select sub issue
                      </option>
                      <option value='1'>Yes</option>
                      <option value='0'>No</option>
                    </Form.Select>
                  </Col>
                  <Col md={6}>
                    <Form.Label className='text-primary me-0 ps-0' column>
                      <span
                        className={
                          isDifferent(legalOrigin.sub_issue, legalPending.sub_issue)
                            ? 'label-highlight'
                            : ''
                        }
                      >
                        Sub Issue
                      </span>
                    </Form.Label>
                    <Form.Select
                      name='sub_issue'
                      value={legalPending.sub_issue ? '1' : '0'}
                      onChange={handleSelectChange}
                    >
                      <option value='' disabled>
                        Select sub issue
                      </option>
                      <option value='1'>Yes</option>
                      <option value='0'>No</option>
                    </Form.Select>
                  </Col>
                </Row>

                <Row className='mb-4'>
                  <Col md={6}>
                    <Form.Label className='text-muted me-0 ps-0' column>
                      Upcoming Flag
                    </Form.Label>
                    <Form.Select disabled value={legalOrigin.upcoming_flag ? '1' : '0'}>
                      <option value='' disabled>
                        Select upcoming flag
                      </option>
                      <option value='1'>Yes</option>
                      <option value='0'>No</option>
                    </Form.Select>
                  </Col>
                  <Col md={6}>
                    <Form.Label className='text-primary me-0 ps-0' column>
                      <span
                        className={
                          isDifferent(legalOrigin.upcoming_flag, legalPending.upcoming_flag)
                            ? 'label-highlight'
                            : ''
                        }
                      >
                        Upcoming Flag
                      </span>
                    </Form.Label>
                    <Form.Select
                      name='upcoming_flag'
                      value={legalPending.upcoming_flag ? '1' : '0'}
                      onChange={handleSelectChange}
                    >
                      <option value='' disabled>
                        Select upcoming flag
                      </option>
                      <option value='1'>Yes</option>
                      <option value='0'>No</option>
                    </Form.Select>
                  </Col>
                </Row>

                <hr />
                {/* REFERENCES */}
                <h3 className='mb-4 mt-5'>References</h3>

                <Row className='mb-4'>
                  <Col md={6}>
                    <Form.Label className='text-muted me-0 ps-0' column>
                      Parent ID
                    </Form.Label>
                    <Form.Control
                      disabled
                      placeholder='Enter parent ID'
                      type='number'
                      value={legalOrigin.parent_id ?? ''}
                    />
                  </Col>
                  <Col md={6}>
                    <Form.Label className='text-primary me-0 ps-0' column>
                      <span
                        className={
                          isDifferent(legalOrigin.parent_id, legalPending.parent_id)
                            ? 'label-highlight'
                            : ''
                        }
                      >
                        Parent ID
                      </span>
                    </Form.Label>
                    <Form.Control
                      placeholder='Enter parent ID'
                      type='number'
                      name='parent_id'
                      value={legalPending.parent_id ?? ''}
                      onChange={handleChange}
                    />
                  </Col>
                </Row>

                <Row className='mb-4'>
                  <Col md={6}>
                    <Form.Label className='text-muted me-0 ps-0' column>
                      Recommended Keyword
                    </Form.Label>
                    <Form.Control
                      disabled
                      as='textarea'
                      rows={2}
                      placeholder='Enter recommended keywords'
                      value={legalOrigin.recommended_keyword || ''}
                    />
                  </Col>
                  <Col md={6}>
                    <Form.Label className='text-primary me-0 ps-0' column>
                      <span
                        className={
                          isDifferent(
                            legalOrigin.recommended_keyword,
                            legalPending.recommended_keyword
                          )
                            ? 'label-highlight'
                            : ''
                        }
                      >
                        Recommended Keyword
                      </span>
                    </Form.Label>
                    <Form.Control
                      as='textarea'
                      rows={2}
                      placeholder='Enter recommended keywords'
                      name='recommended_keyword'
                      value={legalPending.recommended_keyword || ''}
                      onChange={handleChange}
                    />
                  </Col>
                </Row>

                <div className='row pt-5'>
                  <div className='col-lg-12 d-flex justify-content-end gap-3'>
                    <Button variant='default' className='btn-cancel' onClick={handleCancel}>
                      Cancel
                    </Button>
                    <Button
                      variant='primary'
                      onClick={handleSubmit}
                      disabled={isLoadingButton || !isFormValid}
                    >
                      {isLoadingButton ? (
                        <>
                          <Spinner animation='border' size='sm' className='me-2' />
                          Submit
                        </>
                      ) : (
                        'Submit'
                      )}
                    </Button>
                  </div>
                </div>
              </Form>
            ) : (
              <div
                className='d-flex align-items-center justify-content-center'
                style={{height: '50vh'}}
              >
                <Spinner animation='border' role='status' variant='primary' className='me-3' />
                <div className='loading-text'>
                  Loading
                  <span className='dot'>.</span>
                  <span className='dot'>.</span>
                  <span className='dot'>.</span>
                </div>
              </div>
            )}
          </Card.Body>
        </Card>
      </Col>
    </Row>
  )
}

export default LegalTinaComparisonForm
